// ============================================================================
// HR PIN DASHBOARD - Main Component with Ant Design
// Path: frontend/src/components/hr-pin/HrPinDashboard.tsx
// ============================================================================

import React, { useState, useCallback, useEffect } from 'react';
import { ConfigProvider, App as AntApp, theme, Layout } from 'antd';
import { AuthUser, authService } from '../../services/auth.service';
import HrPinHeader from './HrPinHeader';
import HrPinSidebar from './HrPinSidebar';
import HrPinMainContent from './HrPinMainContent';
import styles from './HrPinDashboard.module.css';

const { Content } = Layout;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface HrPinDashboardProps {
  user: AuthUser;
  onLogout: () => void;
}

// Storage key for persisting active tab
const ACTIVE_TAB_KEY = 'hr_pin_active_tab';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const HrPinDashboard: React.FC<HrPinDashboardProps> = ({ user, onLogout }) => {
  // Get initial tab from localStorage or default to dashboard
  const getInitialTab = (): string => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(ACTIVE_TAB_KEY);
      if (saved && [
  'dashboard', 'attendance', 'hr-payroll-report', 'overtime-reports', 
  'hr-leave-approvals', 'salary-slip-requests', 'checkout-requests',
  'attendance-anomaly-detector'
].includes(saved)) {
        return saved;
      }
    }
    return 'dashboard';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Persist active tab to localStorage
  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
  }, [activeTab]);

  // Handle tab change with smooth transition
  const handleTabChange = useCallback((tabId: string) => {
    if (tabId === activeTab || isTransitioning) return;
    
    setIsTransitioning(true);
    
    // Small delay for smooth transition
    requestAnimationFrame(() => {
      setActiveTab(tabId);
      setTimeout(() => setIsTransitioning(false), 100);
    });
  }, [activeTab, isTransitioning]);

  // Handle sidebar collapse
  const handleSidebarCollapse = useCallback((collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
  }, []);

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      // Clear stored tab on logout
      localStorage.removeItem(ACTIVE_TAB_KEY);
      await authService.logout();
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
      onLogout();
    }
  }, [onLogout]);

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#4A90E2',
          borderRadius: 8,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        },
        components: {
          Menu: {
            itemBg: 'transparent',
            itemSelectedBg: 'rgba(74, 144, 226, 0.15)',
            itemSelectedColor: '#4A90E2',
            itemHoverBg: 'rgba(74, 144, 226, 0.08)',
          },
          Button: {
            primaryShadow: '0 2px 8px rgba(74, 144, 226, 0.35)',
          },
          Card: {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          },
        },
      }}
    >
      <AntApp>
        <Layout className={styles.dashboardLayout}>
          {/* Header */}
          <HrPinHeader 
            user={user} 
            onLogout={handleLogout}
            sidebarCollapsed={sidebarCollapsed}
          />

          <Layout className={styles.mainLayout}>
            {/* Sidebar */}
            <HrPinSidebar
              activeTab={activeTab}
              onTabChange={handleTabChange}
              collapsed={sidebarCollapsed}
              onCollapse={handleSidebarCollapse}
              user={user}
            />

            {/* Content Area */}
            <Layout className={styles.contentLayout}>
              <Content className={`${styles.contentArea} ${sidebarCollapsed ? styles.expanded : ''}`}>
                <HrPinMainContent
                  activeTab={activeTab}
                  user={user}
                  onNavigate={handleTabChange}
                  isTransitioning={isTransitioning}
                />
              </Content>
            </Layout>
          </Layout>
        </Layout>
      </AntApp>
    </ConfigProvider>
  );
};

export default HrPinDashboard;